import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import PageHero from '../components/aravi/PageHero';
import Enquiry from '../components/aravi/Enquiry';
import { PHILOSOPHY_PAGE } from '../constants/testIds';
import { PHILOSOPHY_PRINCIPLES, MILESTONES, TEAM_GROUPS } from '../data/pages';

export default function PhilosophyPage() {
  return (
    <div data-testid={PHILOSOPHY_PAGE.root}>
      <PageHero
        eyebrow="Our Philosophy"
        title={<>Discovered<br/>first.</>}
        intro="Aravi was not laid on a piece of land. The land was found, walked, and then planned around — exactly the way an old village would have."
        image="https://images.unsplash.com/photo-1541963463532-d68292c34b19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxuYXR1cmUlMjBsaWZlc3R5bGUlMjByZWFkaW5nJTIwZm9yZXN0fGVufDB8fHx8MTc4MjczMTIzNnww&ixlib=rb-4.1.0&q=85"
      />

      {/* Founder note */}
      <section className="bg-[#F5F1E8] py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1100px] mx-auto">
          <motion.blockquote
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[#18342A] text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] italic font-light"
          >
            &ldquo;We did not want to sell a project. We wanted to give a community the kind of land its grandparents would have recognised — slow, green, honest. So we found that land first.&rdquo;
          </motion.blockquote>
          <div className="mt-10 text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
            — Ravikumar Jatoth, Founder &amp; Chairman
          </div>
        </div>
      </section>

      {/* Why Aravi — the name */}
      <section id="namesake" className="bg-[#9BA58D]/15 py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1200px] mx-auto">
          <div className="grid grid-cols-12 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-5"
            >
              <div className="flex items-center gap-4 mb-6">
                <span className="w-10 h-px bg-[#A66B45]" />
                <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                  Why &ldquo;Aravi&rdquo;
                </span>
              </div>
              <div className="font-serif text-[#18342A] leading-[0.9] font-light" style={{ fontSize: 'clamp(96px, 16vw, 220px)' }}>
                A · Ra · Vi
              </div>
              <div className="mt-6 text-[11px] tracking-[0.4em] uppercase text-[#6D6A61]">
                Sanskrit · From <em className="not-italic text-[#A66B45]">Aranya</em> + <em className="not-italic text-[#A66B45]">Ravi</em>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="col-span-12 lg:col-span-7 space-y-8"
            >
              <p className="font-serif text-[#18342A] text-2xl md:text-3xl leading-snug font-light italic">
                &ldquo;Ara&rdquo; from <span className="not-italic">Aranya</span> — the ancient Sanskrit word for wilderness, for a forest that belongs to itself. &ldquo;Vi&rdquo; from <span className="not-italic">Ravi</span> — the sun. Together, Aravi is the light that lives inside the wild.
              </p>

              <div className="hairline w-24" />

              <div className="space-y-6 text-[#6D6A61] text-base leading-relaxed font-light">
                <p>
                  When our founders first walked this land at dawn, they saw
                  the sun arrive not <em>onto</em> the forest but <em>through</em> it —
                  gold slipping between old canopy trees, settling on the ridgeline,
                  reaching the valley floor last. That was the moment the community
                  earned its name.
                </p>
                <p>
                  Aravi is a promise held inside two syllables: the forest stays,
                  the light finds its way in. Every design decision on this land
                  is asked one question — <span className="italic text-[#18342A]">does it let the wild remain, and the light through?</span>
                </p>
              </div>

              <div className="pt-4 grid grid-cols-2 gap-8">
                <div className="border-l border-[#C9A458]/40 pl-5">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#A66B45]">Aranya</div>
                  <div className="font-serif text-[#18342A] text-lg mt-2 leading-snug">The wilderness that belongs to itself.</div>
                </div>
                <div className="border-l border-[#C9A458]/40 pl-5">
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#A66B45]">Ravi</div>
                  <div className="font-serif text-[#18342A] text-lg mt-2 leading-snug">The sun, the daily arrival of light.</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-[#18342A] text-[#F5F1E8] py-24 lg:py-32 grain-overlay">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto relative z-10">
          <div className="grid grid-cols-12 gap-8 lg:gap-16 items-end mb-16">
            <div className="col-span-12 lg:col-span-8">
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A458]">
                Four Principles
              </span>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight font-light">
                The ground rules<br/>we never break.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#F5F1E8]/10">
            {PHILOSOPHY_PRINCIPLES.map((p, i) => (
              <motion.div
                key={p.number}
                data-testid={PHILOSOPHY_PAGE.principle(i)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#18342A] p-8 lg:p-12 hover:bg-[#1f4233] transition-colors duration-500"
              >
                <div className="flex items-start justify-between mb-10">
                  <span className="text-[#9BA58D] text-[10px] tracking-[0.4em] uppercase">
                    {p.number}
                  </span>
                  <span className="font-serif text-[#C9A458] text-4xl leading-none">·</span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-snug mb-4 font-light">
                  {p.title}
                </h3>
                <p className="text-[#F5F1E8]/65 text-sm leading-relaxed font-light max-w-md">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section id="team" className="bg-[#F5F1E8] py-24 lg:py-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1500px] mx-auto">
          <div className="hairline w-full mb-12" />
          <div className="grid grid-cols-12 gap-8 lg:gap-16 mb-16 items-end">
            <div className="col-span-12 lg:col-span-7">
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
                Leadership
              </span>
              <h2 className="font-serif text-[#18342A] text-4xl md:text-5xl lg:text-6xl mt-6 leading-tight font-light">
                The custodians<br/>behind Aravi.
              </h2>
            </div>
            <p className="col-span-12 lg:col-span-5 text-[#6D6A61] text-base leading-relaxed font-light">
              A deliberately small, deeply local team — founders, executives,
              ecologists and architects — every decision taken in one room.
            </p>
          </div>

          {TEAM_GROUPS.map((group, gi) => (
            <div key={group.title} className={gi === 0 ? '' : 'mt-14'}>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="text-[#C9A458] font-serif text-2xl leading-none">0{gi + 1}</span>
                <h3 className="font-serif text-[#18342A] text-2xl md:text-3xl font-light">
                  {group.title}
                </h3>
                <span className="flex-1 h-px bg-[#C9A458]/25 ml-4" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#18342A]/10">
                {group.members.map((m, i) => (
                  <motion.div
                    key={m.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                    className="bg-[#F5F1E8] p-8 lg:p-10 group flex items-start gap-5"
                  >
                    <div className="w-16 h-16 rounded-full bg-[#18342A] text-[#C9A458] grid place-items-center font-serif text-lg tracking-wider shrink-0 group-hover:bg-[#A66B45] group-hover:text-[#F5F1E8] transition-colors duration-500">
                      {m.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif text-[#18342A] text-xl md:text-2xl leading-tight font-light">
                        {m.name}
                      </h4>
                      <p className="mt-2 text-[11px] tracking-[0.28em] uppercase text-[#A66B45] leading-relaxed">
                        {m.role}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-[#F5F1E8] pb-24 lg:pb-32">
        <div className="px-6 md:px-10 lg:px-16 max-w-[1100px] mx-auto">
          <div className="hairline w-full mb-12" />
          <div className="flex items-baseline justify-between mb-16">
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45]">
              The Aravi Journey
            </span>
            <span className="text-[10px] tracking-[0.4em] uppercase text-[#6D6A61]">
              Origin — Today
            </span>
          </div>

          <div className="relative">
            <div className="absolute left-[14px] top-3 bottom-3 w-px bg-[#C9A458]/40" />
            {MILESTONES.map((m, i) => (
              <motion.div
                key={m.year}
                data-testid={PHILOSOPHY_PAGE.milestone(i)}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative pl-12 pb-12 last:pb-0"
              >
                <span className="absolute left-2 top-2.5 w-3 h-3 rounded-full bg-[#C9A458]" />
                <div className="text-[11px] tracking-[0.4em] uppercase text-[#A66B45] mb-2">
                  {m.year}
                </div>
                <p className="font-serif text-[#18342A] text-xl md:text-2xl leading-snug font-light max-w-2xl">
                  {m.text}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16">
            <Link
              to="/residences"
              className="group inline-flex items-center gap-3 bg-[#18342A] text-[#F5F1E8] px-7 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#C9A458] hover:text-[#18342A] transition-colors"
            >
              See the Two Homes
              <ArrowRight size={14} strokeWidth={1.6} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <Enquiry />
    </div>
  );
}
